import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import PlayPulse from "@/components/PlayPulse";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { fetchReels, sendReelEvent, detectReelSource } from "@/lib/reels";
import ReelsFeed from "@/components/reels/ReelsFeed";
import { fileUrl } from "@/lib/api";
import { useSiteLogo } from "@/lib/useSiteLogo";

const useDesktop = () => {
    const [d, setD] = useState(() => window.matchMedia("(min-width: 900px)").matches);
    useEffect(() => { const mq = window.matchMedia("(min-width: 900px)"); const f = (e) => setD(e.matches); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, []);
    return d;
};

const TOP_BAR = 56;

/** Immersive viewer: fills the whole viewport (above the site header), respects notch/home-bar safe areas on phones,
 *  and on desktop centres one large 9:16-fit player on a dark stage. Swipe/wheel/keys are unchanged (ReelsFeed). */
export default function ReelsPage() {
    const { t, lang, p } = useLang();
    const location = useLocation();
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [err, setErr] = useState(false);
    const [index, setIndex] = useState(0);
    const source = useRef(detectReelSource(location.search, false));
    const cursor = useRef(null);
    const loading = useRef(false);
    const [hasMore, setHasMore] = useState(false);
    const logo = useSiteLogo();
    const desktop = useDesktop();

    useEffect(() => { document.title = `${t("reelsTitle")} | ${t("siteName")}`; }, [lang, t]);
    useEffect(() => {   // lock the page behind the viewer (the viewer owns the gestures)
        const b = document.body.style, h = document.documentElement.style;
        const prev = [b.overflow, b.overscrollBehavior, h.overscrollBehavior, b.touchAction];
        b.overflow = "hidden"; b.overscrollBehavior = "none"; h.overscrollBehavior = "none"; b.touchAction = "none";   // no pull-to-refresh / rubber-band stealing the downward swipe
        return () => { [b.overflow, b.overscrollBehavior, h.overscrollBehavior, b.touchAction] = prev; };
    }, []);

    const load = () => {
        setErr(false); setItems(null); cursor.current = null;
        const v = new URLSearchParams(location.search).get("v");
        return fetchReels({ v }).then(async ({ items: list, next_cursor }) => {
            const i = v ? list.findIndex((x) => x.id === v) : -1;
            if (v && i < 0) {
                try {
                    const r = await api.get("/public/articles/by-ids", { params: { ids: v } });
                    const a = r.data?.items?.[0];
                    if (a?.slug) { navigate(`${p}/article/${a.slug}`, { replace: true }); return; }
                } catch { /* fall through */ }
            }
            cursor.current = next_cursor; setHasMore(!!next_cursor);
            setItems(list);
            setIndex(i >= 0 ? i : 0);
            sendReelEvent("open", { source: source.current, lang });
        }).catch(() => setErr(true));
    };
    useEffect(() => { load(); }, [lang]);   // eslint-disable-line react-hooks/exhaustive-deps
    const loadMore = useCallback(() => {
        if (!cursor.current || loading.current) return Promise.resolve();
        loading.current = true;
        return fetchReels({ cursor: cursor.current }).then(({ items: more, next_cursor }) => {
            cursor.current = next_cursor; setHasMore(!!next_cursor);
            setItems((arr) => { const seen = new Set(arr.map((x) => x.id)); return [...arr, ...more.filter((x) => !seen.has(x.id))]; });
        }).catch(() => {}).finally(() => { loading.current = false; });
    }, []);

    const onIndexChange = (i) => {
        setIndex(i);
        const id = items?.[i]?.id;
        if (id) navigate({ search: `?v=${id}` }, { replace: true });
    };
    const onItemChange = (id, patch) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const Back = lang !== "ar" ? ArrowLeft : ArrowRight;

    return (
        <div className="fixed inset-0 z-[60] bg-[#070d10] text-white flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="reels-page">
            <header className="flex items-center gap-3 px-3 shrink-0" style={{ height: TOP_BAR }}>
                <Link to={`${p}/`} aria-label={t("home")} className="h-11 w-11 -ms-1 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white no-underline" data-testid="reels-back"><Back className="w-5 h-5" /></Link>
                <div className="flex items-center gap-2 min-w-0">
                    {logo ? <img src={fileUrl(logo)} alt="" className="h-9 w-9 rounded-full object-cover border border-white/30 bg-white shrink-0" data-testid="reels-header-photo" /> : <span className="h-9 w-9 rounded-full bg-brand-red shrink-0" />}
                    <span className="font-display font-extrabold text-sm truncate">{t("author")}</span>
                </div>
                <span className="ms-auto inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2 py-1 text-xs font-extrabold text-[#ffb3bd]"><PlayPulse className="play-pulse--light" /> {t("reels")}</span>
            </header>
            <main className="flex-1 min-h-0 flex items-stretch justify-center md:py-3" style={{ "--reel-safe-b": "env(safe-area-inset-bottom)" }}>
                <div className={desktop ? "h-full" : "h-full w-full"} style={desktop ? { maxHeight: "calc(100vh - 80px)" } : { maxWidth: "100vw" }} data-testid="reels-stage">
                    {err && (
                        <div className="h-full flex flex-col items-center justify-center gap-3 text-sm" data-testid="reels-error">
                            <div>{t("loadFail")}</div>
                            <button type="button" onClick={load} className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-full text-sm font-bold min-h-[44px]"><RotateCcw className="w-4 h-4" /> {t("retry")}</button>
                        </div>
                    )}
                    {!err && items === null && <div className="h-full bg-[#11191c] animate-pulse rounded-xl" style={desktop ? { aspectRatio: "9 / 16" } : undefined} data-testid="reels-loading" />}
                    {items && items.length === 0 && <div className="h-full flex items-center justify-center text-gray-300 text-sm px-6 text-center" data-testid="reels-empty">{t("reelsEmpty")}</div>}
                    {items && items.length > 0 && (
                        <ReelsFeed items={items} onItemChange={onItemChange} index={Math.min(index, items.length - 1)} onIndexChange={onIndexChange} source={source.current} autoFocus onNeedMore={loadMore} hasMore={hasMore} fill footer={false} side={desktop} className="h-full" />
                    )}
                </div>
            </main>
        </div>
    );
}
